import DOMPurify from 'dompurify';
import { Token, Tokens, marked } from 'marked';
import { celdaExcelSuperEjecutivo } from '../super-ejecutivo-descarga/super-ejecutivo-descarga.util';
import { ITablaExcelSuperEjecutivo, ITablaSuperEjecutivo } from '../super-ejecutivo.interface';

/**
 * Tablas Markdown de las respuestas y propuestas del Super Ejecutivo. Se separan del resto del texto para dibujarlas
 * con su propio componente, que permite descargarlas; el texto alrededor sigue siendo Markdown.
 */

/** Un pedazo de Markdown: texto que se muestra tal cual o una tabla. */
export type PedazoMarkdownSuperEjecutivo = { tipo: 'markdown'; texto: string } | { tipo: 'tabla'; tabla: ITablaSuperEjecutivo };

/** El texto de una celda sin formato (negritas, enlaces, código), como va al Excel. */
function textoPlano(tokens: readonly Token[]): string {
  return tokens
    .map((token) => {
      if ('tokens' in token && token.tokens) {
        return textoPlano(token.tokens);
      }

      return token.type === 'br' ? ' ' : 'text' in token ? String(token.text) : '';
    })
    .join('')
    .trim();
}

/** El contenido de una celda como HTML seguro: conserva negritas, cursivas, código y enlaces. */
const celdaHtml = (celda: Tokens.TableCell) =>
  DOMPurify.sanitize(marked.parseInline(celda.text, { gfm: true, async: false }) as string, { USE_PROFILES: { html: true } });

function tablaDesdeToken(token: Tokens.Table): ITablaSuperEjecutivo {
  return {
    columnas: token.header.map((celda) => textoPlano(celda.tokens)),
    columnasHtml: token.header.map(celdaHtml),
    filas: token.rows.map((fila) => fila.map((celda) => textoPlano(celda.tokens))),
    filasHtml: token.rows.map((fila) => fila.map(celdaHtml)),
    alineacion: token.align,
  };
}

/** Separa las tablas del Markdown; el texto entre ellas queda igual que como llegó. */
export function separarTablasSuperEjecutivo(markdown: string): PedazoMarkdownSuperEjecutivo[] {
  const pedazos: PedazoMarkdownSuperEjecutivo[] = [];
  let texto = '';

  const cerrarTexto = () => {
    if (texto.trim() !== '') {
      pedazos.push({ tipo: 'markdown', texto });
    }

    texto = '';
  };

  for (const token of marked.lexer(markdown, { gfm: true, breaks: true })) {
    if (token.type === 'table') {
      cerrarTexto();
      pedazos.push({ tipo: 'tabla', tabla: tablaDesdeToken(token as Tokens.Table) });
    } else {
      texto += token.raw;
    }
  }

  cerrarTexto();

  return pedazos;
}

/** El Excel de una tabla: sin encabezado (la tabla no trae título), sus columnas y sus filas, con las cifras como números. */
export function tablaExcelDeTablaSuperEjecutivo(tabla: ITablaSuperEjecutivo): ITablaExcelSuperEjecutivo {
  return {
    encabezado: [],
    columnas: tabla.columnas,
    filas: tabla.filas.map((fila) => fila.map(celdaExcelSuperEjecutivo)),
  };
}
